import { Injectable, inject } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { OnboardingStateService } from '../services/onboarding-state.service';
import { TokenService } from '../services/token.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(): Observable<boolean | UrlTree> {
    return this.authService.getAuthenticatedUser().pipe(
      map((user) => {
        if (!user) {
          return this.router.createUrlTree(['/auth/login']);
        }
        if (
          user.role !== 'admin' &&
          (user.accountStatus === 'pending' || user.accountStatus === 'rejected')
        ) {
          return this.router.createUrlTree(['/onboarding/expediente']);
        }
        // Fase D.3 (RA1-1): el usuario ya está activo pero aún no ha realizado
        // su primer ingreso -> forzarlo a la pantalla de expediente, donde el
        // botón "Ingresar a la Plataforma ABAQ" marcará primerIngresoRealizado.
        // Acceso tolerante: el campo puede no venir en AuthUser todavía.
        if (
          user.role !== 'admin' &&
          user.accountStatus === 'active' &&
          (user as unknown as { primerIngresoRealizado?: boolean }).primerIngresoRealizado === false
        ) {
          return this.router.createUrlTree(['/onboarding/expediente']);
        }
        return true;
      }),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class PublicGuard implements CanActivate {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(): Observable<boolean | UrlTree> {
    return this.authService.getAuthenticatedUser().pipe(
      map((user) => {
        if (!user) {
          return true;
        }
        if (
          user.role !== 'admin' &&
          (user.accountStatus === 'pending' || user.accountStatus === 'rejected')
        ) {
          return this.router.createUrlTree(['/onboarding/expediente']);
        }
        // Fase D.3 (RA1-1): activo sin primer ingreso -> a la pantalla de
        // expediente, no al /home. Acceso tolerante al campo opcional.
        if (
          user.role !== 'admin' &&
          user.accountStatus === 'active' &&
          (user as unknown as { primerIngresoRealizado?: boolean }).primerIngresoRealizado === false
        ) {
          return this.router.createUrlTree(['/onboarding/expediente']);
        }
        if (user.role === 'admin') {
          return this.router.createUrlTree(['/admin/home']);
        }
        if (user.role === 'volunteer') {
          return this.router.createUrlTree(['/volunteer-home']);
        }
        return this.router.createUrlTree(['/home']);
      }),
    );
  }
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingGuard implements CanActivate, CanActivateChild {
  private onboardingService = inject(OnboardingStateService);
  private tokenService = inject(TokenService);
  private router = inject(Router);

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.checkAccess(state.url);
  }

  canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.checkAccess(state.url);
  }

  private checkAccess(url: string): boolean | UrlTree {
    // Si la ruta solicitada es 'expediente' y el usuario ya está autenticado, permitirle consultar su estatus
    if (url.includes('expediente') && this.tokenService.isLoggedIn()) {
      return true;
    }

    // Para acceder a cualquiera de las fases (1 a 4) de onboarding:
    // Es requisito OBLIGATORIO haber completado el paso inicial en /auth/signup
    // (donde se selecciona el rol y se definen correo y contraseña válidos).
    const p1 = this.onboardingService.state.phase1;
    if (p1.email && p1.password && p1.userType) {
      return true;
    }

    // Si se intenta entrar directamente sin pasar por /auth/signup al inicio, regresar a /auth/signup
    return this.router.createUrlTree(['/auth/signup']);
  }
}

